/**
 * Audit-log writer for test-kit results (write-only trail, called
 * outside React). Appends to the legacy localStorage trail
 * (`reefapp.testKits.history`, capped at 500 entries) AND best-effort
 * appends to the Pi (`/api/userdata/test-kit-history`, same cap) via a
 * read-modify-write. Pi failures are logged, never thrown — the local
 * trail is the offline fallback. Single-operator app, so the naive
 * concurrency of the read-modify-write is acceptable.
 */
import type { ParameterKey, CalculationResult } from '@reefapp/shared';

import { userDataApi } from './userdata-api';

/** Legacy browser-only trail — kept in sync, never deleted. */
const LEGACY_HISTORY_KEY = 'reefapp.testKits.history';

/** `/api/userdata` namespace holding the audit trail on the Pi. */
export const TEST_KIT_HISTORY_NAMESPACE = 'test-kit-history';

const MAX_ENTRIES = 500;

export interface TestKitLogPayload {
  kitSlug: string;
  brand: string;
  testName: string;
  parameterKey: ParameterKey;
  parameter: string;
  result: CalculationResult;
  unit: string;
  notes?: string;
}

interface HistoryEntry {
  ts: string;
  kitSlug: string;
  brand: string;
  testName: string;
  parameterKey: string;
  parameter: string;
  result: CalculationResult;
  unit: string;
  notes?: string;
}

/** Append in place with the shared 500-entry cap (drops oldest). */
function appendCapped(arr: unknown[], entry: HistoryEntry): void {
  arr.push(entry);
  if (arr.length > MAX_ENTRIES) {
    arr.splice(0, arr.length - MAX_ENTRIES);
  }
}

function appendToLocalHistory(entry: HistoryEntry): void {
  try {
    const raw = window.localStorage.getItem(LEGACY_HISTORY_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const arr: unknown[] = Array.isArray(parsed) ? parsed : [];
    appendCapped(arr, entry);
    window.localStorage.setItem(LEGACY_HISTORY_KEY, JSON.stringify(arr));
  } catch {
    // storage full or disabled — non-fatal
  }
}

/**
 * Best-effort read-modify-write append to the Pi copy. Skipped (with a
 * console note) whenever the agent is unreachable — the entry still
 * lives in the localStorage trail.
 */
async function appendToServerHistory(entry: HistoryEntry): Promise<void> {
  const res = await userDataApi.get(TEST_KIT_HISTORY_NAMESPACE);
  if (!res.ok) {
    console.info('[test-kit-log] Pi unreachable, kept locally:', res.error);
    return;
  }
  const arr: unknown[] = Array.isArray(res.value.data)
    ? [...res.value.data]
    : [];
  appendCapped(arr, entry);
  const put = await userDataApi.put(TEST_KIT_HISTORY_NAMESPACE, arr);
  if (!put.ok) {
    console.info('[test-kit-log] non-fatal Pi write error:', put.error);
  }
}

export async function writeTestKitLog(
  payload: TestKitLogPayload,
): Promise<void> {
  const entry: HistoryEntry = {
    ts: new Date().toISOString(),
    ...payload,
  };

  // Mirror to localStorage first — always succeeds offline.
  appendToLocalHistory(entry);

  // Best-effort Pi append (never throws — Result-typed seam).
  await appendToServerHistory(entry);
}
